import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { updateReportExercisePayload } from "../lib/reportExerciseFirestore";
import { sanitizeForFirestore } from "../lib/workoutReportMutations";
import ExerciseNameLink from "../../plan/components/ExerciseNameLink";
import NewColors from "../../../shared/theme/NewColors";

function coerceNumber(raw) {
  if (raw === "" || raw == null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function cloneReportData(rd) {
  return JSON.parse(JSON.stringify(rd ?? null));
}

/** @param {(string|number|null)[]} vals */
function firstNonNilWeight(...vals) {
  const v = vals.find((x) => x !== "" && x != null && !Number.isNaN(Number(x)));
  return Number.isFinite(Number(v)) ? Number(v) : "";
}

/** @param {object} workout - reportExercise doc with id */
export default function EditWorkoutDialog({
  open,
  onClose,
  workout,
  organizationId,
  clientId,
}) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [saveError, setSaveError] = useState(null);

  const titleSubtitle = useMemo(() => {
    const d =
      workout?.completedOn ??
      workout?.createdOn ??
      (workout?.completedOnSeconds ? workout.completedOnSeconds : null);
    if (!d) return "Edit workout session";
    const date = d instanceof Date ? d : new Date(d);
    return `Edit workout — ${date.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    })}`;
  }, [workout]);

  useEffect(() => {
    if (open && workout?.reportData) {
      setDraft(cloneReportData(workout.reportData));
      setFormError("");
      setSaveError(null);
    }
  }, [open, workout?.id, workout?.reportData]);

  if (!organizationId || !clientId || !workout?.id) {
    return null;
  }

  const exercises = draft?.exercises ?? [];

  const updateNormalExercise = (exerciseIndex, patch) => {
    setDraft((prev) => {
      const next = cloneReportData(prev);
      Object.assign(next.exercises[exerciseIndex], patch);
      return next;
    });
  };

  const updateNormalSet = (exerciseIndex, setIndex, patch) => {
    setDraft((prev) => {
      const next = cloneReportData(prev);
      const sets = [...(next.exercises[exerciseIndex].sets ?? [])];
      sets[setIndex] = { ...sets[setIndex], ...patch };
      next.exercises[exerciseIndex] = {
        ...next.exercises[exerciseIndex],
        sets,
      };
      return next;
    });
  };

  const updateSuperNested = (exerciseIndex, setIndex, nestedIndex, patch) => {
    setDraft((prev) => {
      const next = cloneReportData(prev);
      const supersetsSets = [...(next.exercises[exerciseIndex].sets ?? [])];
      const nested = [...(supersetsSets[setIndex]?.exercises ?? [])];
      nested[nestedIndex] = { ...nested[nestedIndex], ...patch };
      supersetsSets[setIndex] = {
        ...supersetsSets[setIndex],
        exercises: nested,
      };
      next.exercises[exerciseIndex] = {
        ...next.exercises[exerciseIndex],
        sets: supersetsSets,
      };
      return next;
    });
  };

  const updateSuperNestedSetRow = (exerciseIndex, roundIndex, nestedIndex, nestedSetIndex, setPatch) => {
    setDraft((prev) => {
      const next = cloneReportData(prev);
      const supersetsSets = [...(next.exercises[exerciseIndex].sets ?? [])];
      const nested = [...(supersetsSets[roundIndex]?.exercises ?? [])];
      const ex = nested[nestedIndex];
      const setsArr = [...(ex.sets ?? [])];
      setsArr[nestedSetIndex] = { ...setsArr[nestedSetIndex], ...setPatch };
      nested[nestedIndex] = { ...ex, sets: setsArr };
      supersetsSets[roundIndex] = {
        ...supersetsSets[roundIndex],
        exercises: nested,
      };
      next.exercises[exerciseIndex] = {
        ...next.exercises[exerciseIndex],
        sets: supersetsSets,
      };
      return next;
    });
  };

  /** Nested exercises inside supersets carry their own per-round `sets` rows. */

  const validateBeforeSave = () => {
    if (!draft?.exercises?.length) {
      setFormError("Nothing to save — this workout has no exercises payload.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;
    setBusy(true);
    setSaveError(null);
    try {
      const payload = sanitizeForFirestore(draft);
      await updateReportExercisePayload({
        organizationId,
        clientId,
        workoutId: workout.id,
        reportData: payload,
      });
      onClose(true);
    } catch (e) {
      setSaveError(e?.message ?? "Failed to save changes.");
    } finally {
      setBusy(false);
    }
  };

  const renderNormalExercise = (ex, exerciseIndex) => {
    const name =
      ex.exerciseName ??
      ex.exercise ??
      `Exercise`;
    return (
      <Accordion key={`ex-${exerciseIndex}`} disableGutters defaultExpanded sx={{ bgcolor: NewColors.sectionBackground }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ minWidth: 0 }}>
            <ExerciseNameLink exerciseId={ex.exercise} name={String(name)} variant="subtitle2" />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Catalog ID: <Box component="span" sx={{ fontFamily: "monospace" }}>{String(ex.exercise ?? "")}</Box>
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ex.skipped === true}
                    onChange={(_, checked) =>
                      updateNormalExercise(exerciseIndex, { skipped: checked, completed: checked ? false : ex.completed })
                    }
                  />
                }
                label="Exercise skipped"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ex.completed === true}
                    disabled={ex.skipped === true}
                    onChange={(_, checked) =>
                      updateNormalExercise(exerciseIndex, { completed: checked, skipped: checked ? false : ex.skipped })
                    }
                  />
                }
                label="Exercise marked complete"
              />
            </Stack>
            {(ex.sets ?? []).map((set, setIndex) => (
              <Box
                key={`set-${setIndex}`}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Set {setIndex + 1}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1 }}>
                  <TextField
                    label="Weight"
                    fullWidth
                    value={firstNonNilWeight(set.weight, set.workingWeight)}
                    onChange={(e) => {
                      const n = coerceNumber(e.target.value);
                      updateNormalSet(exerciseIndex, setIndex, { weight: n ?? "", workingWeight: n ?? "" });
                    }}
                  />
                  <TextField
                    label="Target reps"
                    fullWidth
                    value={set.reps != null ? String(set.reps) : ""}
                    onChange={(e) => {
                      const n = coerceNumber(e.target.value);
                      updateNormalSet(exerciseIndex, setIndex, { reps: n == null ? null : Math.round(n) });
                    }}
                  />
                  <TextField
                    label="Completed reps"
                    fullWidth
                    value={set.completedReps != null ? String(set.completedReps) : ""}
                    onChange={(e) => {
                      const n = coerceNumber(e.target.value);
                      updateNormalSet(exerciseIndex, setIndex, { completedReps: n == null ? null : Math.round(n) });
                    }}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={set.skipped === true}
                        onChange={(_, checked) =>
                          updateNormalSet(exerciseIndex, setIndex, {
                            skipped: checked,
                            completed: checked ? false : set.completed,
                          })
                        }
                      />
                    }
                    label="Set skipped"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={set.completed === true}
                        disabled={set.skipped === true}
                        onChange={(_, checked) =>
                          updateNormalSet(exerciseIndex, setIndex, {
                            completed: checked,
                            skipped: checked ? false : set.skipped,
                          })
                        }
                      />
                    }
                    label="Set completed"
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSuperSetExercise = (ex, exerciseIndex) => (
    <Accordion key={`ss-${exerciseIndex}`} disableGutters defaultExpanded sx={{ bgcolor: NewColors.sectionBackground }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 700 }}>{ex.superSetName || "Super set"}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {(ex.sets ?? []).map((round, roundIdx) => (
          <Box key={`round-${roundIdx}`} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Round {roundIdx + 1}
            </Typography>
            {(round.exercises ?? []).map((nested, nestedIdx) => {
              const name =
                nested.exerciseName ??
                nested.exercise ??
                `Exercise`;
              return (
                <Box key={`nested-${nestedIdx}`} sx={{ ml: { sm: 1 }, mb: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <ExerciseNameLink
                    exerciseId={nested.exercise}
                    name={String(name)}
                    variant="subtitle2"
                  />
                </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={nested.skipped === true}
                          onChange={(_, checked) =>
                            updateSuperNested(exerciseIndex, roundIdx, nestedIdx, {
                              skipped: checked,
                              completed: checked ? false : nested.completed,
                            })
                          }
                        />
                      }
                      label="Skipped"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={nested.completed === true}
                          disabled={nested.skipped === true}
                          onChange={(_, checked) =>
                            updateSuperNested(exerciseIndex, roundIdx, nestedIdx, {
                              completed: checked,
                              skipped: checked ? false : nested.skipped,
                            })
                          }
                        />
                      }
                      label="Complete"
                    />
                  </Stack>
                  {(nested.sets ?? []).map((ns, nsIdx) => (
                    <Box
                      key={`nss-${nsIdx}`}
                      sx={{
                        borderLeft: "3px solid",
                        borderColor: "divider",
                        pl: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Set {nsIdx + 1}
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          label="Weight"
                          size="small"
                          fullWidth
                          value={firstNonNilWeight(ns.weight, ns.workingWeight)}
                          onChange={(e) => {
                            const n = coerceNumber(e.target.value);
                            updateSuperNestedSetRow(exerciseIndex, roundIdx, nestedIdx, nsIdx, {
                              weight: n ?? "",
                              workingWeight: n ?? "",
                            });
                          }}
                        />
                        <TextField
                          label="Target reps"
                          size="small"
                          fullWidth
                          value={ns.reps != null ? String(ns.reps) : ""}
                          onChange={(e) => {
                            const n = coerceNumber(e.target.value);
                            updateSuperNestedSetRow(exerciseIndex, roundIdx, nestedIdx, nsIdx, {
                              reps: n == null ? null : Math.round(n),
                            });
                          }}
                        />
                        <TextField
                          label="Done reps"
                          size="small"
                          fullWidth
                          value={ns.completedReps != null ? String(ns.completedReps) : ""}
                          onChange={(e) => {
                            const n = coerceNumber(e.target.value);
                            updateSuperNestedSetRow(exerciseIndex, roundIdx, nestedIdx, nsIdx, {
                              completedReps: n == null ? null : Math.round(n),
                            });
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={ns.skipped === true}
                              onChange={(_, checked) =>
                                updateSuperNestedSetRow(exerciseIndex, roundIdx, nestedIdx, nsIdx, {
                                  skipped: checked,
                                  completed: checked ? false : ns.completed,
                                })
                              }
                            />
                          }
                          label="Set skipped"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={ns.completed === true}
                              disabled={ns.skipped === true}
                              onChange={(_, checked) =>
                                updateSuperNestedSetRow(exerciseIndex, roundIdx, nestedIdx, nsIdx, {
                                  completed: checked,
                                  skipped: checked ? false : ns.skipped,
                                })
                              }
                            />
                          }
                          label="Set done"
                        />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog open={open} onClose={() => (!busy ? onClose(false) : null)} fullWidth maxWidth="md">
      <DialogTitle>{titleSubtitle}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
        {!draft ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading workout…</Typography>
          </Box>
        ) : (
          <>
            {formError ? <Alert severity="warning">{formError}</Alert> : null}
            {saveError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Changes save to the same Firestore record the mobile app uses. Use exercise migration tools if you need to swap
              the underlying catalog exercise for historical sessions.
            </Typography>
            <Stack spacing={2}>
              {exercises.map((ex, idx) =>
                ex.type === "super_set" ? renderSuperSetExercise(ex, idx) : renderNormalExercise(ex, idx),
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={busy} onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || !draft} onClick={handleSave}>
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
